import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../AppIcon';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-secondary text-secondary-foreground pt-16 pb-12 border-t border-border mt-auto">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 pb-12 border-b border-secondary-foreground/15">
          {/* Brand Info */}
          <div className="lg:col-span-2 space-y-4">
            <Link to="/" className="flex items-center space-x-3 group">
              <img
                src="/assets/bytepairs-logo.png"
                alt="Byte Pairs Logo"
                className="h-10 w-10 rounded-full border border-primary/30 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <span className="text-xl font-black tracking-tight text-secondary-foreground">
                Byte <span className="text-primary">Pairs</span>
              </span>
            </Link>
            <p className="text-secondary-foreground/75 text-sm leading-relaxed max-w-sm">
              Custom software development company in Dharapuram, Tamil Nadu. We engineer billing software, CRM dashboards, mobile apps, and business automations. Pay once, own the code forever.
            </p>
            <div className="flex items-center space-x-3 pt-2">
              <a
                href="https://www.facebook.com/profile.php?id=61591945857535&sk=about"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-background/40 hover:bg-primary/20 hover:text-primary flex items-center justify-center text-secondary-foreground/70 transition-colors duration-200 border border-border/50"
                aria-label="Byte Pairs on Facebook"
              >
                <Icon name="Facebook" size={16} />
              </a>
              <a
                href="https://www.instagram.com/bytepairs?igsh=MTlobnUzMGdhNHRkbA=="
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-background/40 hover:bg-primary/20 hover:text-primary flex items-center justify-center text-secondary-foreground/70 transition-colors duration-200 border border-border/50"
                aria-label="Byte Pairs on Instagram"
              >
                <Icon name="Instagram" size={16} />
              </a>
              <a
                href="https://www.linkedin.com/company/109882504"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-background/40 hover:bg-primary/20 hover:text-primary flex items-center justify-center text-secondary-foreground/70 transition-colors duration-200 border border-border/50"
                aria-label="Byte Pairs on LinkedIn"
              >
                <Icon name="Linkedin" size={16} />
              </a>
            </div>
          </div>

          {/* Core Services */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider text-secondary-foreground mb-4">
              Services
            </h4>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to="/services/custom-software" className="text-secondary-foreground/75 hover:text-primary transition-colors">
                  Custom Software
                </Link>
              </li>
              <li>
                <Link to="/services/web-apps" className="text-secondary-foreground/75 hover:text-primary transition-colors">
                  Web Applications
                </Link>
              </li>
              <li>
                <Link to="/services/mobile-apps" className="text-secondary-foreground/75 hover:text-primary transition-colors">
                  iOS & Android Apps
                </Link>
              </li>
              <li>
                <Link to="/services/ai-automation" className="text-secondary-foreground/75 hover:text-primary transition-colors">
                  WhatsApp Automation
                </Link>
              </li>
              <li>
                <Link to="/services/website-seo" className="text-secondary-foreground/75 hover:text-primary transition-colors">
                  Website & SEO Studio
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-primary font-medium hover:underline inline-flex items-center space-x-1 pt-1">
                  <span>View All Services</span>
                  <Icon name="ArrowRight" size={12} />
                </Link>
              </li>
            </ul>
          </div>

          {/* Company & Knowledge */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider text-secondary-foreground mb-4">
              Company
            </h4>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to="/about-mission-values" className="text-secondary-foreground/75 hover:text-primary transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/team-excellence-expertise-personality" className="text-secondary-foreground/75 hover:text-primary transition-colors">
                  Our Team
                </Link>
              </li>
              <li>
                <Link to="/technology-showcase-interactive-capabilities" className="text-secondary-foreground/75 hover:text-primary transition-colors">
                  Tech Capabilities
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-secondary-foreground/75 hover:text-primary transition-colors">
                  Engineering Blog
                </Link>
              </li>
              <li>
                <Link to="/global-collaboration" className="text-secondary-foreground/75 hover:text-primary transition-colors">
                  Global Collaboration
                </Link>
              </li>
              <li>
                <Link to="/contact-engagement-hub" className="text-secondary-foreground/75 hover:text-primary transition-colors">
                  Contact Hub
                </Link>
              </li>
              <li>
                <Link to="/locations" className="text-primary font-medium hover:underline inline-flex items-center space-x-1 pt-1">
                  <span>Locations Directory</span>
                  <Icon name="ArrowRight" size={12} />
                </Link>
              </li>
            </ul>
          </div>

          {/* Regional Service Areas & Contact */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider text-secondary-foreground mb-4">
              Contact & Coverage
            </h4>
            <ul className="space-y-2.5 text-sm text-secondary-foreground/75 mb-4">
              <li className="flex items-start space-x-2">
                <Icon name="MapPin" size={16} className="text-primary shrink-0 mt-0.5" />
                <span>Dharapuram, Tamil Nadu, India - 638656</span>
              </li>
              <li className="flex items-center space-x-2">
                <Icon name="Phone" size={16} className="text-primary shrink-0" />
                <a href="tel:+916374241548" className="hover:text-primary transition-colors">
                  +91 6374241548
                </a>
              </li>
              <li className="flex items-center space-x-2">
                <Icon name="Mail" size={16} className="text-primary shrink-0" />
                <a href="mailto:bytepairs2026@gmail.com" className="hover:text-primary transition-colors">
                  bytepairs2026@gmail.com
                </a>
              </li>
            </ul>
            <div className="pt-2 border-t border-secondary-foreground/10">
              <span className="text-xs uppercase font-semibold text-secondary-foreground/90 block mb-2">
                Key Regional Coverage
              </span>
              <div className="flex flex-wrap gap-1.5 text-xs">
                <Link to="/software-development-company-dharapuram" className="text-secondary-foreground/80 hover:text-primary transition-colors font-medium">
                  Dharapuram (HQ)
                </Link>
                <span className="text-secondary-foreground/40">•</span>
                <Link to="/software-development-company-tiruppur" className="text-secondary-foreground/70 hover:text-primary transition-colors">
                  Tiruppur
                </Link>
                <span className="text-secondary-foreground/40">•</span>
                <Link to="/software-development-company-coimbatore" className="text-secondary-foreground/70 hover:text-primary transition-colors">
                  Coimbatore
                </Link>
                <span className="text-secondary-foreground/40">•</span>
                <Link to="/software-development-company-chennai" className="text-secondary-foreground/70 hover:text-primary transition-colors">
                  Chennai
                </Link>
                <span className="text-secondary-foreground/40">•</span>
                <Link to="/software-development-company-bangalore" className="text-secondary-foreground/70 hover:text-primary transition-colors">
                  Bangalore
                </Link>
                <span className="text-secondary-foreground/40">•</span>
                <Link to="/locations" className="text-primary font-semibold hover:underline">
                  All Locations & Global →
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between text-xs text-secondary-foreground/60 gap-4">
          <p>
            © {currentYear} Byte Pairs. All rights reserved. Direct developer collaboration, pay once, own the code forever.
          </p>
          <div className="flex items-center space-x-4">
            <Link to="/contact-engagement-hub" className="hover:text-primary transition-colors">
              Support
            </Link>
            <span>•</span>
            <Link to="/services" className="hover:text-primary transition-colors">
              Solutions
            </Link>
            <span>•</span>
            <Link to="/blog" className="hover:text-primary transition-colors">
              Insights
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
